/**
 * The chat overlay: the input bar and the scrolling journal (Slice 5).
 *
 * This module owns only the DOM wiring of the on-screen chat surfaces; the markup
 * and styling live in `index.html` (the same split the splash uses). It reads no
 * Pixi and no socket - it takes an `onSay` callback to push a typed line up, and
 * exposes {@link ChatHandle.append} so the network layer can add an inbound line,
 * and {@link ChatHandle.isInputFocused} so `main.ts` can gate the movement keys
 * while the player is typing.
 *
 * It also owns the **text-colour settings** popover: the speech-bubble button
 * beside the input opens
 * a swatch grid where the player picks the hue their *own* overhead speech renders
 * in (the default is plain white - the UO hue-0 look). The pick is reported through
 * `onPickHue`; `main.ts` persists it and forwards it to the server, which stamps
 * it onto the player's overhead lines. The swatches reuse the splash's `.hues` styling.
 *
 * Two hardening choices live here because this is a public room:
 * - The journal is built **only** with `textContent` and `createElement`, never
 *   `innerHTML`, so a line like `<img onerror=…>` is shown verbatim as text and
 *   never parsed as markup. (The server also caps length; Pixi `Text` is
 *   plain-text-safe for the overhead surface.)
 * - The retained line count is bounded so a long-lived session does not grow the
 *   DOM without limit; the journal still "persists the session" for any practical
 *   sitting.
 */

import { createTabs } from './ui/tabs';
import type { WireCardRarity } from './net/protocol';

/** Upper bound on journal lines kept in the DOM; older lines are pruned. */
const MAX_JOURNAL_LINES = 300;

/**
 * The wall-clock stamp attached to every journal line, as `HH:MM` on a 24-hour
 * clock in the viewer's local time. The reference chrome hides it visually while
 * retaining it in accessible text. Speech is ephemeral (there is no
 * server timestamp on the wire), so this is the client's *receive* time - the
 * moment the line landed in the journal, which is what a reader wants ("when did
 * I see this"). Kept minute-resolution: seconds add noise to a chat glance, and a
 * bank-sitting session is measured in minutes. Pure so tests can pass a fixed date.
 */
function formatClockTime(now: Date): string {
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Smallest the draggable journal may shrink to, in px (a couple of lines). */
const MIN_JOURNAL_HEIGHT = 64;
/** Gap kept between the grown journal's top edge and the viewport top, in px. */
const JOURNAL_TOP_MARGIN = 24;
/** One keyboard nudge on the focused resize bar, in px. */
const JOURNAL_KEYBOARD_STEP = 20;

/** Compact touch layouts start with the journal behind a launcher button. */
const MOBILE_CHAT_QUERY = '(max-width: 680px), (pointer: coarse)';

/** One swatch the text-colour picker offers: a UO hue id plus a CSS preview. */
interface TextSwatch {
  /** 1-based UO `hues.mul` id; `0` is "no hue" - the plain-white default. */
  hue: number;
  /** Approximate CSS colour for the swatch button; the real text uses the baked ramp. */
  css: string;
  /** Accessible label / tooltip for the swatch. */
  label: string;
}

/**
 * The curated palette the text-colour button offers. White (hue `0`, "no hue
 * applied") leads as the default, followed by a spread of legible hues. Each hue
 * id was picked by sampling the baked `hues.mul` ramp at the same gradient step
 * the renderer uses for speech (see {@link ../render/huePalette}'s
 * `SAMPLE_FRACTION`), and each `css` is that exact sampled colour - so a swatch
 * button always shows the colour the text will actually render in (a blue button
 * really makes blue text, not purple). Keep `css` in sync with the ramp if a hue
 * id changes: it is the preview, but the authoritative recolour is the ramp.
 */
const TEXT_SWATCHES: readonly TextSwatch[] = [
  { hue: 0, css: '#ffffff', label: 'Default (white)' },
  { hue: 39, css: '#c63929', label: 'Crimson' },
  { hue: 44, css: '#c66b29', label: 'Orange' },
  { hue: 1260, css: '#e7c631', label: 'Amber' },
  { hue: 49, css: '#c69429', label: 'Gold' },
  { hue: 274, css: '#39b552', label: 'Green' },
  { hue: 100, css: '#4a73c6', label: 'Azure' },
  { hue: 398, css: '#214aa5', label: 'Blue' },
  { hue: 20, css: '#bd4ac6', label: 'Magenta' },
  { hue: 114, css: '#8431bd', label: 'Purple' },
];

/** Options for {@link initChat}; all optional so headless tests can pass none. */
export interface ChatOptions {
  /**
   * Called with the chosen UO hue id (`0` = white default) when the player picks
   * a swatch from the text-colour button. The caller persists it and tells the
   * server, so it colours the player's later overhead lines.
   */
  onPickHue?: (hue: number) => void;
  /** The hue selected when the overlay mounts (a restored preference); defaults to `0`. */
  initialHue?: number;
  /**
   * Called when the player right-clicks a journal line whose speaker can be ignored
   * (the per-viewer ignore list), with the speaker's display name and the pointer's
   * viewport coords so the caller can position the "Ignore <name>" popover. Action/
   * emote lines carry no speaker and never fire this. The caller owns the popover and
   * the self-guard; absent => a right-click on the journal is left to the browser.
   */
  onIgnoreSpeaker?: (name: string, x: number, y: number) => void;
  /**
   * Whether the given speaker is actually ignorable (the self/herald veto, centralized
   * in `canIgnoreName`). Consulted before the journal's right-click suppresses the
   * browser default menu: a non-ignorable line (your own speech, a system herald) is
   * left to the native menu so right-click-copy still works, and never opens the
   * popover. Absent => every speaker line is treated as ignorable (the popover's own
   * guard still applies).
   */
  canIgnoreSpeaker?: (name: string) => boolean;
  /** Open the shared-card viewer when a player activates a card link in the journal. */
  onViewCard?: (card: ChatCardRef) => void;
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** Per-line options for {@link ChatHandle.append}; omit for ordinary speech. */
export interface AppendOptions {
  /**
   * Render as an action/emote line (the "New Cosmetic Award!" emote, PRD #101):
   * the `Name:` speaker prefix is omitted so the journal reads just the (already
   * `*…*`-wrapped) text, matching the prefix-less overhead line. Defaults to
   * `false`, so all ordinary speech still shows `Name: text`.
   */
  action?: boolean;
  /** Semantic presentation kind; ordinary player speech is the default. */
  kind?: ChatLineKind;
  /**
   * Zero or more server-validated card references embedded in this ordinary speech
   * line. Each is matched to its own `[Card Name]` token and rendered as an
   * independent, correctly-coloured link - so a message can link any number of
   * cards without the extras inheriting the first card's identity or rarity.
   */
  cards?: readonly ChatCardInput[];
  /** Stable server-stamped moderation identity for removable player speech. */
  authorKey?: string;
}

/** Semantic kinds used by the System filter and reference-specific typography. */
export type ChatLineKind = 'speech' | 'herald' | 'action' | 'system';

/** The two views exposed by the reference-style tab strip. */
type ChatSection = 'general' | 'system';

/** The handle `main.ts` drives the chat overlay through. */
export interface ChatHandle {
  /**
   * Append one spoken line to the journal, attributed to `name`. `lineCss` carries
   * the semantic colour for prefix-less action lines. Auto-
   * scrolls to the newest line when the journal is already pinned to the bottom,
   * so reading back history is not yanked away by new chatter. Pass
   * `{ action: true }` to drop the speaker prefix for an action/emote line.
   */
  append(name: string, text: string, lineCss: string, opts?: AppendOptions): void;
  /** Remove all retained journal lines authored by `authorKey`. */
  removeByAuthor(authorKey: string): number;
  /** Whether the chat input currently holds keyboard focus (gates movement keys). */
  isInputFocused(): boolean;
  /**
   * Populate the chat input with `text`, focus it, and drop the caret at the end,
   * so the "say last" hotkey (Ctrl+Q) can put the player's last line back for a
   * one-key resend or an edit. This is a **recall into the input**, never a send -
   * the player presses Enter themselves. It replaces any in-progress unsent text
   * (least-surprising for a deliberate recall); the caller supplies the text
   * (`main.ts`'s {@link ../lastSaid.LastSaid}), keeping this a pure DOM write.
   */
  recallInput(text: string): void;
  /**
   * Insert one owned card reference at the current composer selection and focus
   * the input. The visible `[Card Name]` stays editable, while submit carries the
   * structured identity only if that exact token is still present.
   */
  insertCard(card: ChatCardInput): void;
}

/** The structured card identity sent with one chat line. */
export interface ChatCardRef {
  cardId: string;
  rarity: WireCardRarity;
}

/** Binder-supplied card data needed to build the visible composer token. */
export interface ChatCardInput extends ChatCardRef {
  name: string;
}

/**
 * Wire the chat input, journal, and text-colour settings in the given document.
 * `onSay` is called with the raw typed line when the player presses Enter on a
 * non-empty input (the caller trims/validates and the server caps length). Enter
 * with the game focused focuses the input so the player can type without reaching
 * for the mouse; Enter in the input sends and returns focus to the game so
 * movement keys work again immediately; Escape abandons the line and returns
 * focus to the game.
 */
export function initChat(
  onSay: (text: string, cards?: ChatCardRef[]) => void,
  opts: ChatOptions = {},
): ChatHandle {
  const chatOptions = opts;
  const doc = opts.doc ?? document;
  const journal = doc.getElementById('journal')!;
  const input = doc.getElementById('chat-input') as HTMLInputElement;
  const root = doc.getElementById('chat');
  const panel = doc.getElementById('chat-panel') as HTMLElement | null;
  const launcher = doc.getElementById('chat-launcher') as HTMLButtonElement | null;
  const collapse = doc.getElementById('chat-collapse') as HTMLButtonElement | null;
  const unreadBadge = doc.getElementById('chat-unread') as HTMLElement | null;
  const unreadStatus = doc.getElementById('chat-unread-status') as HTMLElement | null;
  const compactQuery = doc.defaultView?.matchMedia?.(MOBILE_CHAT_QUERY);
  let compact = compactQuery?.matches ?? false;
  let expanded = !compact;
  let unreadCount = 0;
  let syncResize = (): void => {};
  let activeSection: ChatSection = 'general';
  // Every card the player has inserted into the current draft, in insertion order.
  // Each carries the exact `[Card Name]` token it was written as; a card is only sent
  // (and only survives an edit) while its token is still present in the input, so
  // deleting a token drops just that card and any number can ride one line.
  let pendingCards: (ChatCardRef & { token: string })[] = [];

  const renderUnread = (): void => {
    if (!unreadBadge || !launcher) return;
    unreadBadge.hidden = unreadCount === 0;
    unreadBadge.textContent = unreadCount > 99 ? '99+' : String(unreadCount);
    if (unreadStatus) {
      unreadStatus.textContent =
        unreadCount === 0
          ? ''
          : `${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}`;
    }
    launcher.setAttribute(
      'aria-label',
      unreadCount === 0
        ? 'Open chat'
        : `Open chat, ${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}`,
    );
  };

  const setExpanded = (next: boolean, focusComposer = false): void => {
    expanded = next;
    root?.classList.toggle('chat-expanded', expanded);
    root?.classList.toggle('chat-collapsed', !expanded);
    launcher?.setAttribute('aria-expanded', String(expanded));
    collapse?.setAttribute('aria-expanded', String(expanded));
    if (panel) {
      panel.setAttribute('aria-hidden', String(!expanded));
      panel.inert = !expanded;
    }
    if (expanded) {
      unreadCount = 0;
      for (const line of Array.from(journal.querySelectorAll<HTMLElement>('.journal-line'))) {
        delete line.dataset.unread;
      }
      renderUnread();
      syncResize();
      if (focusComposer) input.focus();
    } else if (doc.activeElement === input) {
      input.blur();
    }
  };

  launcher?.addEventListener('click', () => setExpanded(true, true));
  collapse?.addEventListener('click', () => setExpanded(false));
  compactQuery?.addEventListener?.('change', (event) => {
    compact = event.matches;
    if (compact) journal.style.height = '';
    setExpanded(!compact);
  });
  setExpanded(!compact);

  const showSection = (section: ChatSection): void => {
    activeSection = section;
    for (const line of Array.from(journal.querySelectorAll<HTMLElement>('.journal-line'))) {
      line.hidden = activeSection === 'system' && line.dataset.kind === 'speech';
    }
  };

  createTabs<ChatSection>(
    [
      { button: doc.getElementById('chat-tab-general'), value: 'general' },
      { button: doc.getElementById('chat-tab-system'), value: 'system' },
    ],
    activeSection,
    showSection,
  );

  const submit = (): void => {
    const text = input.value;
    input.value = '';
    if (text.trim().length > 0) {
      const cards = collectPresentCards(text, pendingCards);
      onSay(text, cards.length > 0 ? cards : undefined);
    }
    pendingCards = [];
  };

  input.addEventListener('input', () => {
    pendingCards = keepPresentCards(input.value, pendingCards);
  });

  input.addEventListener('keydown', (e) => {
    // Keep keystrokes typed into the chat from leaking to the game's window-level
    // movement listeners - so typing "wasd" never walks the character.
    e.stopPropagation();
    if (e.key === 'Enter') {
      submit();
      input.blur();
    } else if (e.key === 'Escape') {
      input.value = '';
      input.blur();
    }
  });

  // Enter while the game has focus jumps into the chat input (UO-ish), so a line
  // can be typed without a click. When the input already has focus its own
  // handler stops the event before it reaches here.
  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && doc.activeElement !== input) {
      setExpanded(true);
      input.focus();
      e.preventDefault();
    }
  });

  // Right-click a journal line to ignore its speaker (the per-viewer ignore list).
  // One delegated listener on the journal survives the line pruning; it only fires
  // for a line that recorded a speaker (`data-speaker`), so action/emote lines are
  // left alone and the browser's default menu is suppressed *only* on an ignorable
  // line - never globally. A speaker the viewer cannot ignore (their own name, a
  // system herald - `canIgnoreSpeaker`) falls through to the native menu, so
  // right-click-copy still works on your own and herald lines and no dead popover opens.
  if (opts.onIgnoreSpeaker) {
    journal.addEventListener('contextmenu', (e) => {
      const target = e.target as HTMLElement | null;
      const line = target?.closest?.('.journal-line') as HTMLElement | null;
      const speaker = line?.dataset.speaker;
      if (!speaker) return;
      if (opts.canIgnoreSpeaker && !opts.canIgnoreSpeaker(speaker)) return;
      e.preventDefault();
      opts.onIgnoreSpeaker!(speaker, e.clientX, e.clientY);
    });
  }

  initSettings(doc, opts);
  syncResize = initResize(doc);
  syncResize();

  return {
    append(name, text, lineCss, opts) {
      // Snap to newest only if the reader is already at the bottom.
      const atBottom = journal.scrollTop + journal.clientHeight >= journal.scrollHeight - 4;

      const line = doc.createElement('div');
      line.className = 'journal-line';
      line.dataset.kind = opts?.kind ?? (opts?.action ? 'action' : 'speech');
      if (opts?.authorKey) line.dataset.authorKey = opts.authorKey;
      line.hidden = activeSection === 'system' && line.dataset.kind === 'speech';
      // Ordinary speech uses the chosen hue as speaker identity while the body stays
      // muted and readable. Prefix-less action lines still carry their semantic hue.
      if (opts?.action) line.style.color = lineCss;
      // An accessible `HH:MM` receive-time stamp leads every line in the DOM. The
      // reference chrome visually clips it while assistive technology retains it.
      const time = doc.createElement('span');
      time.className = 'journal-time';
      time.textContent = `${formatClockTime(new Date())} `;
      line.appendChild(time);
      // An action/emote line has no speaker: it reads just the `*…*` text, matching
      // the prefix-less overhead line. Ordinary speech keeps the `Name: ` prefix and
      // records its speaker on the line, so a right-click can resolve who to ignore
      // (the per-viewer ignore list). Action lines carry no speaker and stay
      // un-ignorable - they are heralds/emotes, never chatter.
      if (!opts?.action) {
        line.dataset.speaker = name;
        const who = doc.createElement('span');
        who.className = 'journal-who';
        who.textContent = `${name}: `;
        line.appendChild(who);
      }
      const body = doc.createElement('span');
      body.className = 'journal-body';
      if (opts?.action) body.style.color = lineCss;
      const cards = opts?.action ? [] : (opts?.cards ?? []);
      if (cards.length > 0) {
        renderCardBody(doc, body, text, cards, (card) => {
          chatOptions.onViewCard?.({ cardId: card.cardId, rarity: card.rarity });
        });
      } else {
        // textContent, never innerHTML: a line is shown as text, never parsed.
        body.textContent = text;
      }
      line.appendChild(body);
      journal.appendChild(line);

      if (compact && !expanded) {
        line.dataset.unread = 'true';
        unreadCount += 1;
        renderUnread();
      }

      while (journal.childElementCount > MAX_JOURNAL_LINES && journal.firstChild) {
        journal.removeChild(journal.firstChild);
      }
      if (atBottom) journal.scrollTop = journal.scrollHeight;
    },
    removeByAuthor(authorKey) {
      let removed = 0;
      let removedUnread = 0;
      for (const line of Array.from(journal.querySelectorAll<HTMLElement>('.journal-line'))) {
        if (line.dataset.authorKey !== authorKey) continue;
        if (line.dataset.unread === 'true') removedUnread += 1;
        line.remove();
        removed += 1;
      }
      if (removedUnread > 0) {
        unreadCount = Math.max(0, unreadCount - removedUnread);
        renderUnread();
      }
      return removed;
    },
    isInputFocused() {
      return doc.activeElement === input;
    },
    recallInput(text) {
      setExpanded(true);
      input.value = text;
      pendingCards = [];
      input.focus();
      // Caret at the end so Enter resends straight away, or the player edits from
      // the tail. setSelectionRange is safe on a plain text input (jsdom included).
      const end = input.value.length;
      input.setSelectionRange(end, end);
    },
    insertCard(card) {
      setExpanded(true);
      const token = `[${card.name}]`;
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      input.value = `${input.value.slice(0, start)}${token}${input.value.slice(end)}`;
      // Accumulate, don't replace: inserting a second card keeps the first, so a
      // single line can carry any number of card links. A prior insert whose token
      // was edited out is pruned first (it is no longer in the input).
      pendingCards = keepPresentCards(input.value, pendingCards);
      pendingCards.push({ cardId: card.cardId, rarity: card.rarity, token });
      input.focus();
      const caret = start + token.length;
      input.setSelectionRange(caret, caret);
    },
  };
}

/** Count non-overlapping occurrences of `token` in `text` (empty token => 0). */
function countOccurrences(text: string, token: string): number {
  if (token === '') return 0;
  let count = 0;
  let from = 0;
  for (;;) {
    const at = text.indexOf(token, from);
    if (at < 0) return count;
    count += 1;
    from = at + token.length;
  }
}

/**
 * Keep only the pending cards whose `[Card Name]` token still appears in `text`,
 * count-aware and in insertion order: if a token appears fewer times than there are
 * cards claiming it, only the leading ones (up to that many) survive. Used to prune
 * a draft as the player edits and to pick the cards a submitted line carries, so the
 * structured identities can never outnumber the visible tokens.
 */
function keepPresentCards<T extends { token: string }>(text: string, cards: readonly T[]): T[] {
  const budget = new Map<string, number>();
  const kept: T[] = [];
  for (const card of cards) {
    let remaining = budget.get(card.token);
    if (remaining === undefined) remaining = countOccurrences(text, card.token);
    if (remaining > 0) {
      kept.push(card);
      budget.set(card.token, remaining - 1);
    } else {
      budget.set(card.token, 0);
    }
  }
  return kept;
}

/** Strip the composer bookkeeping token off the cards a submitted line will carry. */
function collectPresentCards(
  text: string,
  cards: readonly (ChatCardRef & { token: string })[],
): ChatCardRef[] {
  return keepPresentCards(text, cards).map((card) => ({
    cardId: card.cardId,
    rarity: card.rarity,
  }));
}

/**
 * Render `text` into `body`, turning each card's `[Card Name]` token into its own
 * rarity-coloured view link while the rest stays plain text. The scan is left to
 * right so every link lands at its real position; cards sharing a token are consumed
 * in insertion order and a token is matched at most as many times as it has cards, so
 * a repeated name links only where the sender attached a card. Longest token first so
 * a longer name is never shadowed by a shorter prefix. Only `createTextNode`/
 * `createElement` are used, never `innerHTML`, so the line is shown as text.
 */
function renderCardBody(
  doc: Document,
  body: HTMLElement,
  text: string,
  cards: readonly ChatCardInput[],
  onView: (card: ChatCardInput) => void,
): void {
  const queues = new Map<string, ChatCardInput[]>();
  for (const card of cards) {
    const token = `[${card.name}]`;
    const queue = queues.get(token);
    if (queue) queue.push(card);
    else queues.set(token, [card]);
  }
  const tokens = Array.from(queues.keys()).sort((a, b) => b.length - a.length);

  let plain = '';
  const flush = (): void => {
    if (plain === '') return;
    body.appendChild(doc.createTextNode(plain));
    plain = '';
  };

  let i = 0;
  while (i < text.length) {
    let matched = false;
    for (const token of tokens) {
      const queue = queues.get(token)!;
      if (queue.length > 0 && text.startsWith(token, i)) {
        flush();
        body.appendChild(makeCardLink(doc, queue.shift()!, token, onView));
        i += token.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      plain += text[i];
      i += 1;
    }
  }
  flush();
}

/** Build one rarity-coloured card-view link for a `[Card Name]` token. */
function makeCardLink(
  doc: Document,
  card: ChatCardInput,
  token: string,
  onView: (card: ChatCardInput) => void,
): HTMLButtonElement {
  const link = doc.createElement('button');
  link.type = 'button';
  link.className = `journal-card-link rarity-${card.rarity}`;
  link.textContent = token;
  link.setAttribute('aria-label', `View ${card.rarity} ${card.name} card`);
  link.addEventListener('click', () => onView(card));
  return link;
}

/**
 * Wire the journal's drag-to-resize handle. The `#chat` overlay is anchored to
 * the viewport bottom, so growing the journal's height extends it *upward* - the
 * say bar stays put while the history reveals more lines. Dragging the bar up
 * makes the journal taller, down makes it shorter, clamped between
 * {@link MIN_JOURNAL_HEIGHT} and what fits above the say bar (leaving
 * {@link JOURNAL_TOP_MARGIN} of breathing room at the top). Up/Down and Home/End
 * provide the same adjustment from the focused separator. The handle is optional
 * in the DOM, so the minimal headless chat fixture can skip it.
 */
function initResize(doc: Document): () => void {
  const handle = doc.getElementById('chat-resize');
  const journal = doc.getElementById('journal');
  if (!handle || !journal) return () => {};

  const view = doc.defaultView ?? window;
  let startY = 0;
  let startHeight = 0;

  // The most the journal can grow to: from its current top edge up to the top
  // margin. Computed at grab time so it tracks the live window size and where
  // the overlay currently sits.
  const maxHeight = (): number => {
    const fromBottom = view.innerHeight - journal.getBoundingClientRect().bottom;
    return Math.max(MIN_JOURNAL_HEIGHT, view.innerHeight - JOURNAL_TOP_MARGIN - fromBottom);
  };

  const setHeight = (height: number): void => {
    const maximum = maxHeight();
    const clamped = Math.min(maximum, Math.max(MIN_JOURNAL_HEIGHT, height));
    journal.style.height = `${clamped}px`;
    handle.setAttribute('aria-valuemin', String(MIN_JOURNAL_HEIGHT));
    handle.setAttribute('aria-valuemax', String(Math.round(maximum)));
    handle.setAttribute('aria-valuenow', String(Math.round(clamped)));
    handle.setAttribute('aria-valuetext', `${Math.round(clamped)} pixels tall`);
  };

  const onMove = (e: PointerEvent): void => {
    // Pointer up from the start lengthens the journal (dragging toward the top).
    setHeight(startHeight + (startY - e.clientY));
  };

  const onUp = (e: PointerEvent): void => {
    handle.classList.remove('dragging');
    handle.releasePointerCapture?.(e.pointerId);
    doc.removeEventListener('pointermove', onMove);
    doc.removeEventListener('pointerup', onUp);
  };

  handle.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    startY = e.clientY;
    startHeight = journal.getBoundingClientRect().height;
    handle.classList.add('dragging');
    handle.setPointerCapture?.(e.pointerId);
    doc.addEventListener('pointermove', onMove);
    doc.addEventListener('pointerup', onUp);
  });

  handle.addEventListener('keydown', (e) => {
    const current = journal.getBoundingClientRect().height;
    let next: number | null = null;
    if (e.key === 'ArrowUp') next = current + JOURNAL_KEYBOARD_STEP;
    if (e.key === 'ArrowDown') next = current - JOURNAL_KEYBOARD_STEP;
    if (e.key === 'Home') next = MIN_JOURNAL_HEIGHT;
    if (e.key === 'End') next = maxHeight();
    if (next === null) return;
    e.preventDefault();
    setHeight(next);
  });

  const syncRenderedHeight = (): void => {
    const height = journal.getBoundingClientRect().height;
    if (height > 0) setHeight(height);
  };

  syncRenderedHeight();
  return syncRenderedHeight;
}

/**
 * Wire the text-colour button and its swatch popover. The button toggles the
 * panel; the panel holds one round swatch per {@link TEXT_SWATCHES} entry in a
 * single-select radiogroup (the splash's `.hues` look). Picking a swatch marks it, closes the
 * panel, and reports the hue through `onPickHue`. A click anywhere outside, or
 * Escape, dismisses the panel. Both the cog and panel are optional in the DOM, so
 * the headless chat tests (which mount only the journal + input) skip this wiring.
 */
function initSettings(doc: Document, opts: ChatOptions): void {
  const cog = doc.getElementById('chat-settings') as HTMLButtonElement | null;
  const panel = doc.getElementById('chat-settings-panel');
  if (!cog || !panel) return;

  let current = opts.initialHue ?? 0;

  const markSelected = (hue: number): void => {
    for (const b of Array.from(panel.querySelectorAll('button'))) {
      b.setAttribute('aria-pressed', String(Number(b.dataset.hue) === hue));
    }
  };

  for (const swatch of TEXT_SWATCHES) {
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.dataset.hue = String(swatch.hue);
    btn.style.background = swatch.css;
    btn.title = swatch.label;
    btn.setAttribute('aria-label', swatch.label);
    btn.setAttribute('aria-pressed', String(swatch.hue === current));
    btn.addEventListener('click', () => {
      current = swatch.hue;
      markSelected(current);
      setOpen(false);
      opts.onPickHue?.(current);
    });
    panel.appendChild(btn);
  }

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    cog.setAttribute('aria-expanded', String(open));
  };

  cog.addEventListener('click', (e) => {
    e.stopPropagation();
    setOpen(panel.hidden);
  });

  // A click outside the panel (and not on the cog) closes it; so does Escape.
  doc.addEventListener('click', (e) => {
    if (panel.hidden) return;
    const target = e.target as Node;
    if (!panel.contains(target) && target !== cog) setOpen(false);
  });
  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !panel.hidden) setOpen(false);
  });
}
